import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getLFCData, setLFCDataProc} from '_logics/LFCUtil';
import axios from 'axios';
import {NotificationApi} from '../api/notificationApi';
import awsConfiguration from 'awsConfiguration';
import {
  ACTIVE_STATUS,
  RECEIVE_MAIL_TYPE,
  SQL_IDS,
  normalizeReceiveMailType
} from '../constants/alertSettingsConfig';

interface AlertSettingsActionsProps {
  alertId: string;
  open: boolean;
  onClose: () => void;
  reload: () => Promise<void>;
  enqueueSnackbar: any;
  emails: any[];
  onSaveAndOpenNotification?: (newAlertId: string) => void;

  // Form values
  active: boolean;
  alertName: string;
  machine: string;
  rule: string;
  processType: string;
  scheduleType: string;
  days: string[];
  from: string;
  to: string;
  interval: string;
  fixTime: string;
  isPdf: boolean;

  // Form setters
  setActive: (value: boolean) => void;
  setAlertName: (value: string) => void;
  setMachine: (value: string) => void;
  setRule: (value: string) => void;
  setProcessType: (value: string) => void;
  setScheduleType: (value: string) => void;
  setDays: (value: string[]) => void;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
  setInterval: (value: string) => void;
  setFixTime: (value: string) => void;
  setIsPdf: (value: boolean) => void;

  // Notification management
  notifications: any[];
  setNotifications: (value: any[]) => void;

  // Validation
  validateAll: (formValues: any) => boolean;

  // Reset functions
  resetForm: () => void;
  resetNotifications: () => void;
  clearValidationErrors: () => void;
}

/**
 * 新しく作成されたアラートを検索（リトライ付き）
 * アラート名とデバイスIDでマッチング
 */
const findNewlyCreatedAlert = async (
  alertName: string,
  deviceId: string,
  enqueueSnackbar: any,
  cancelToken: any,
  t: any
): Promise<any> => {
  const maxRetries = 3;
  const retryDelay = 500; // ミリ秒

  // 正規化関数
  const normalize = (str: string) => String(str || '').trim();
  const normalizedAlertName = normalize(alertName);
  const normalizedDeviceId = normalize(deviceId);

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[findNewlyCreatedAlert] 試行 ${attempt}/${maxRetries}`, {
          alertName: normalizedAlertName,
          deviceId: normalizedDeviceId,
          timestamp: new Date().toISOString()
        });
      }

      // 2回目以降は少し待機（DBの反映待ち）
      if (attempt > 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }

      // アラート一覧を取得
      const alerts = await getLFCData({
        snack: enqueueSnackbar,
        sql_id: 137,
        parameters: {},
        cancelToken: cancelToken,
        t
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`[findNewlyCreatedAlert] 取得したアラート件数: ${alerts.length}`);
        if (alerts.length > 0) {
          console.log(
            '[findNewlyCreatedAlert] 最新5件:',
            alerts.slice(-5).map((a: any) => ({
              alert_id: a.alert_id,
              alert_name: a.alert_name,
              device_id: a.device_id,
              device_id_type: typeof a.device_id
            }))
          );
        }
      }

      // アラート名とデバイスIDが一致するものを検索（最新のものを取得）
      const matchingAlerts = alerts.filter((alert: any) => {
        const alertNameMatch = normalize(alert.alert_name) === normalizedAlertName;
        const deviceIdMatch = String(alert.device_id) === normalizedDeviceId;

        return alertNameMatch && deviceIdMatch;
      });

      if (matchingAlerts.length > 0) {
        // alert_idが最大のもの（最新）を返す
        const latestAlert = matchingAlerts.reduce((latest: any, current: any) =>
          current.alert_id > latest.alert_id ? current : latest
        );

        if (process.env.NODE_ENV === 'development') {
          console.log('[findNewlyCreatedAlert] 検索成功（完全一致）:', {
            alert_id: latestAlert.alert_id,
            alert_name: latestAlert.alert_name,
            device_id: latestAlert.device_id,
            attempt
          });
        }

        return latestAlert;
      }

      // 完全一致が見つからない場合
      if (attempt < maxRetries) {
        console.warn(
          `[findNewlyCreatedAlert] アラートが見つかりません。リトライします... (${attempt}/${maxRetries})`
        );
        continue; // 次の試行へ
      }

      // === フォールバック検索: アラート名のみで検索 ===
      console.warn(
        '[findNewlyCreatedAlert] 完全一致で見つからなかったため、アラート名のみで検索します'
      );

      const nameOnlyMatches = alerts.filter(
        (alert: any) => normalize(alert.alert_name) === normalizedAlertName
      );

      if (nameOnlyMatches.length > 0) {
        // 最新のものを返す
        const latestAlert = nameOnlyMatches.reduce((latest: any, current: any) =>
          current.alert_id > latest.alert_id ? current : latest
        );

        console.warn('[findNewlyCreatedAlert] アラート名のみで一致するものが見つかりました:', {
          alert_id: latestAlert.alert_id,
          alert_name: latestAlert.alert_name,
          device_id: latestAlert.device_id,
          expected_device_id: normalizedDeviceId
        });

        return latestAlert;
      }

      // 最終試行でも見つからない場合、詳細情報をログ出力
      console.error('[findNewlyCreatedAlert] アラート検索失敗 - 詳細:', {
        searchCriteria: {
          alertName: normalizedAlertName,
          deviceId: normalizedDeviceId
        },
        totalAlerts: alerts.length,
        allDeviceIds: [...new Set(alerts.map((a: any) => a.device_id))],
        allAlertNames: [...new Set(alerts.map((a: any) => a.alert_name))].slice(-5) // 最新5件
      });

      return null;
    } catch (error) {
      console.error(`[findNewlyCreatedAlert] エラー (試行 ${attempt}/${maxRetries}):`, error);

      if (attempt === maxRetries) {
        throw error;
      }

      // 次の試行へ
      console.warn(`[findNewlyCreatedAlert] リトライします... (${attempt}/${maxRetries})`);
    }
  }

  return null;
};

/**
 * アラート設定のAPI操作を管理するフック
 * onSubmit, onDelete, データ取得を提供
 */
export const useAlertSettingsActions = (props: AlertSettingsActionsProps) => {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingRecipients, setExistingRecipients] = useState<
    {recipients: string; receive_mail: 1 | 2}[]
  >([]);

  /**
   * 設定保存処理
   * 新規登録と編集で処理を分離し、APIを最適化
   * @param openNotification - 保存後に通知先設定ダイアログを開くかどうか
   */
  const onSubmit = async (openNotification: boolean = false) => {
    // バリデーションチェック
    if (
      !props.validateAll({
        alertName: props.alertName,
        machine: props.machine,
        days: props.days,
        scheduleType: props.scheduleType,
        from: props.from,
        to: props.to,
        interval: props.interval,
        fixTime: props.fixTime
      })
    ) {
      return;
    }

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    let newAlertId = '';

    // basePayload と payload を try ブロックの外で宣言
    let basePayload: any = null;
    let payload: any = null;

    try {
      setIsLoading(true);

      // 基本payload（共通フィールド）
      basePayload = {
        is_active: props.active ? ACTIVE_STATUS.ACTIVE : ACTIVE_STATUS.INACTIVE,
        alert_name: props.alertName,
        device_id: props.machine,
        rule_type: Number(props.rule),
        process_type: Number(props.processType),
        schedule_type: Number(props.scheduleType),
        schedule: props.days.filter(day =>
          ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].includes(day)
        ),
        is_pdf: props.isPdf,
        user_pool_id: awsConfiguration.UserPoolId
      };

      // スケジュールタイプに応じて条件付きフィールドを追加
      payload =
        props.scheduleType === '1'
          ? {
              ...basePayload,
              start_time: props.from,
              end_time: props.to,
              alert_interval: Number(props.interval)
            }
          : {
              ...basePayload,
              fix_time: props.fixTime
            };

      /* ========================================
       * 新規登録フロー
       * ======================================== */
      if (props.alertId === '-1') {
        // Step 1: アラート設定を作成
        await setLFCDataProc({
          snack: props.enqueueSnackbar,
          invoke_name: 'SetAlertScheduler',
          parameters: payload,
          name: `${t('アラート設定 保存')}`,
          cancelToken: source.token,
          t
        });

        // Step 2: データをリロードして最新のalert_idを取得
        await props.reload();

        // Step 3: 通知先設定を開く場合
        if (openNotification && props.onSaveAndOpenNotification) {
          // 一覧から新しく作成されたアラートを検索
          try {
            const newAlert = await findNewlyCreatedAlert(
              props.alertName,
              props.machine,
              props.enqueueSnackbar,
              source.token,
              t
            );

            if (newAlert?.alert_id) {
              // 通知先設定ダイアログを開く
              props.onSaveAndOpenNotification(newAlert.alert_id);
            } else {
              throw new Error('作成されたアラートが見つかりませんでした');
            }
          } catch (searchError) {
            console.error('新規アラート検索エラー:', searchError);
            props.enqueueSnackbar(
              t(
                'アラートは作成されましたが、通知先設定を開けませんでした。一覧から編集してください。'
              ),
              {variant: 'warning', persist: true}
            );
            props.onClose();
          }
        } else {
          // 通常の保存の場合はダイアログを閉じる
          props.onClose();
        }
      } else {
        /* ========================================
         * 編集フロー
         * ======================================== */
        // Step 1: アラート設定を更新
        await setLFCDataProc({
          snack: props.enqueueSnackbar,
          invoke_name: 'SetAlertScheduler',
          parameters: {...payload, alert_id: props.alertId},
          name: `${t('アラート設定 保存')}`,
          cancelToken: source.token,
          t
        });

        // Step 2: 通知先の差分を適用
        if (existingRecipients.length > 0 || props.notifications.length > 0) {
          await NotificationApi.applyNotificationChanges(
            props.alertId,
            existingRecipients,
            props.notifications,
            props.emails,
            props.enqueueSnackbar,
            source.token,
            t
          );
        }

        props.enqueueSnackbar(t('設定を更新しました。'), {variant: 'success'});

        // リロードして閉じる
        await props.reload();
        props.onClose();
      }
    } catch (error) {
      console.error('アラート設定保存エラー:', error);

      // 開発環境用の詳細ログ
      if (process.env.NODE_ENV === 'development') {
        console.error('詳細:', {
          alertId: props.alertId,
          payload: basePayload,
          error
        });
      }

      // ユーザーフレンドリーなエラーメッセージ
      if (props.alertId === '-1') {
        props.enqueueSnackbar(t('message.設定の新規登録に失敗しました。'), {
          variant: 'error',
          persist: true
        });
      } else {
        props.enqueueSnackbar(t('message.設定の更新に失敗しました。'), {
          variant: 'error',
          persist: true
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 設定削除処理
   */
  const onDelete = async () => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    try {
      setIsLoading(true);

      /* Lambda関数を呼び出してアラートスケジューラーを削除 */
      await setLFCDataProc({
        snack: props.enqueueSnackbar,
        invoke_name: 'DeleteAlertScheduler',
        parameters: {alert_id: props.alertId},
        name: `${t('アラート設定 削除')}`,
        cancelToken: source.token,
        t
      });

      await props.reload();

      props.enqueueSnackbar(t('message.設定の削除が完了しました。'), {variant: 'success'});
      props.onClose();
    } catch (error) {
      console.error('アラート設定削除エラー:', error);
      props.enqueueSnackbar(t('message.設定の削除に失敗しました。'), {variant: 'error'});
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 編集時のみデータ取得
   */
  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    let isMounted = true;

    const fetchData = async () => {
      if (!props.open) return;

      try {
        if (isMounted) {
          setIsLoading(true);
          props.resetForm();
          props.resetNotifications();
          props.clearValidationErrors();
        }

        if (props.alertId !== '-1') {
          const promises = [
            getLFCData({
              snack: props.enqueueSnackbar,
              sql_id: SQL_IDS.GET_ALERT_SETTINGS,
              parameters: {alertId: props.alertId},
              cancelToken: source.token,
              t
            })
              .then(ds => {
                if (!isMounted) return;

                if (!ds || !ds[0]) {
                  throw new Error('データが取得できませんでした');
                }

                const data = ds[0];

                props.setActive(data['Active?'] === ACTIVE_STATUS.ACTIVE);
                props.setAlertName(data.alert_name || '');
                props.setRule(data.Rule ? String(data.Rule) : '1');
                props.setMachine(data.machine || '');
                props.setInterval(data.alert_interval ? String(data.alert_interval) : '1');

                const scheduleTypeValue = data.schedule_type ? String(data.schedule_type) : '1';
                props.setScheduleType(scheduleTypeValue);

                if (scheduleTypeValue === '2') {
                  const fixTimeValue = data.fix_time?.split(':').slice(0, 2).join(':') || '09:00';
                  props.setFixTime(fixTimeValue);
                } else {
                  const fromValue = data.start_time?.split(':').slice(0, 2).join(':') || '08:00';
                  const toValue = data.end_time?.split(':').slice(0, 2).join(':') || '22:00';
                  props.setFrom(fromValue);
                  props.setTo(toValue);
                }

                props.setIsPdf(data.is_pdf === true || data.is_pdf === 1);

                if (data.schedule && typeof data.schedule === 'string') {
                  const parsedDays = data.schedule
                    .replace(/[()']/g, '')
                    .replace(/\.\s*/g, ',')
                    .split(',')
                    .map((day: string) => day.trim())
                    .filter((day: string) => day !== '');
                  props.setDays(parsedDays);
                }

                if (Array.isArray(data.recipient_details)) {
                  // Store existing recipients for later comparison
                  setExistingRecipients(data.recipient_details);

                  const newNotifications = data.recipient_details
                    .map((item: any) => {
                      if (!item || !item.recipients) return null;
                      const emailData = props.emails.find(e => e.id === item.recipients);
                      if (!emailData) return null;
                      return {
                        email: emailData.id,
                        receiveMailType: normalizeReceiveMailType(item.receive_mail)
                      };
                    })
                    .filter((item: any) => item !== null)
                    .sort((a: any, b: any) => a.email.localeCompare(b.email));

                  props.setNotifications(newNotifications);
                }
              })
              .catch(error => {
                if (axios.isCancel(error)) {
                  console.log('データ取得がキャンセルされました');
                  return;
                }
                console.error('データ取得エラー:', error);
                if (isMounted) {
                  props.enqueueSnackbar(t('message.データの取得に失敗しました'), {
                    variant: 'error'
                  });
                }
              })
          ];

          await Promise.allSettled(promises);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('データ取得処理エラー:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel('リクエストをキャンセルしてページ移動');
    };
  }, [props.open, props.alertId, props.emails]);

  return {
    onSubmit,
    onDelete,
    isLoading
  };
};
